

















































































































































/* General */
import Vue from 'vue'

/* Mixins */

/* Components */
import DeletePersonModal from '../components/delete-person-modal.vue'
import StandardEmailInput from '@/components/global/inputs/standard-email-input.vue'

/* Helpers */

import RouteAssistant from '@/helpers/route-assistant'
import AppSumoAssistant from '@/helpers/appsumo-assistant'
import TypeAssistant from '@/helpers/type-assistant'

/* Data types */
import { AccountRoles } from '@/types/defined-types'
import { ICompaniesInputCreateAccountMembership } from '@/api/types/request.types'
import {
  ICompanyRoster,
  IItemForSelection,
  IRosterPerson,
} from '@/api/types/response.types'

/* API */
import AccountMembershipsAPI from '@/api/modules/account-memberships.api'
import CompaniesAPI from '@/api/modules/companies.api'
import { AccountRoleTypes } from '@/types'

export default Vue.extend({
  name: 'PeopleRoster',

  components: {
    DeletePersonModal,
    StandardEmailInput,
  },

  props: {
    company: {
      type: Object as () => ICompanyRoster,
      required: true,
    },
  },

  data() {
    return {
      AppSumoAssistant,
      TypeAssistant,
      AccountRoleTypes,
      isSubmitting: false,
      AccountRoles,
      RouteAssistant,
      sentInviteAccountMembershipIDs: [] as number[],
      invitee: {
        accountRoleID: 1,
        sendInvitation: true,
      } as ICompaniesInputCreateAccountMembership,
      inviteeEmailValid: false,

      /* Delete person modal data */
      personToDelete: {} as IRosterPerson,
      showDeletePersonModal: false,

      renderSelectAccountMembershipID: null,
      quickAddSelectRollover: false,

      renderDropdownAccountMembershipID: null,
    }
  },

  computed: {
    accountID(): number {
      return parseInt(this.$route.params.accountId, 10)
    },
    availableRoles(): IItemForSelection[] {
      return this.$permissions.availableRolesToAdmin
    },
    inviteeInvalid(): boolean {
      return !(this.invitee.accountRoleID && this.inviteeEmailValid)
    },
    placeholderText(): string {
      return 'Invite to ' + this.company.name + ' by email...'
    },
  },

  mounted() {
    this.resetInvitee()
  },

  methods: {
    resetInvitee() {
      this.invitee = {
        accountRoleID: 1,
        sendInvitation: true,
      } as ICompaniesInputCreateAccountMembership
    },
    onQuickAddSelectRollover() {
      this.quickAddSelectRollover = true
    },
    onSelectRollover(accountMembershipID: number) {
      this.renderSelectAccountMembershipID = accountMembershipID
    },
    isSelectRolledOver(accountMembershipID: number) {
      return (
        this.renderSelectAccountMembershipID &&
        this.renderSelectAccountMembershipID === accountMembershipID
      )
    },
    onMoreDropdownRollover(accountMembershipID: number) {
      this.renderDropdownAccountMembershipID = accountMembershipID
    },
    isMoreDropdownRolledOver(accountMembershipID: number) {
      return (
        this.renderDropdownAccountMembershipID &&
        this.renderDropdownAccountMembershipID === accountMembershipID
      )
    },
    hasMemberships(person: IRosterPerson): boolean {
      return !(person.projectCount === 0 && person.mailboxCount === 0)
    },
    inviteSent(accountMembershipID: number): boolean {
      return this.sentInviteAccountMembershipIDs.includes(accountMembershipID)
    },
    getSendInviteText(person: IRosterPerson): string {
      return person.hasRegistered ? 'Email Password Reset' : 'Email Invitation'
    },
    getProjectMailboxDisplay(person: IRosterPerson): string {
      let result: string = ''

      // Guest roles do not have access to mailbox
      const mailboxAcccess = person.accountRoleID !== 4

      if (!this.hasMemberships(person)) {
        return mailboxAcccess
          ? 'Add to projects & mailboxes'
          : 'Add to projects'
      }

      if (person.projectCount === 1) {
        result += '1 project'
      } else if (person.projectCount > 1) {
        result += person.projectCount + ' projects'
      }

      if (person.projectCount > 0 && person.mailboxCount > 0) {
        result += ' & '
      }
      if (person.mailboxCount === 1) {
        result += '1 mailbox'
      } else if (person.mailboxCount > 1) {
        result += person.mailboxCount + ' mailboxes'
      }

      return result
    },
    initiateDeletePerson(person: IRosterPerson) {
      this.personToDelete = person
      this.showDeletePersonModal = true
    },
    getAccountRoleName(accountRoleID: Number) {
      return AccountRoles.find(a => a.id === accountRoleID).name
    },
    async updateAccountRole(person: IRosterPerson) {
      try {
        // Hand-assign the new account role name since we aren't refreshing from server...
        person.accountRoleName = this.getAccountRoleName(person.accountRoleID)
        const response = await AccountMembershipsAPI.put_UpdateRole(
          person.accountMembershipID,
          person.accountRoleID,
        )
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
        this.$emit('account-role-update-disallowed')
      }
    },
    async convertToGuestUser(person: IRosterPerson) {
      this.$confirm('', {
        center: true,
        confirmButtonText: 'Yes, Convert',
        confirmButtonClass: 'el-button--strawberry',
        cancelButtonText: 'Cancel',
        cancelButtonClass: 'el-button--text',
        type: 'warning',
        title: `Convert "${person.name}" to Guest user?`,
      }).then(() => {
        AccountMembershipsAPI.put_UpdateRole(
          person.accountMembershipID,
          AccountRoleTypes.GUEST,
        )
          .then(response => {
            person.accountRoleID = AccountRoleTypes.GUEST
            this.$messaging.showSuccess(response.message)
          })
          .catch(error => {
            this.$error.emit(error, true)
            this.$emit('account-role-update-disallowed')
          })
      })
    },
    async sendSignInEmail(accountMembershipID: number) {
      try {
        const response = await AccountMembershipsAPI.get_SendSignInEmail(
          accountMembershipID,
        )
        this.sentInviteAccountMembershipIDs.push(accountMembershipID)
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },
    async createAccountMembership() {
      if (
        !(await AppSumoAssistant.isUnderAccountMembershipsLimit(
          this.$router,
          this.$permissions,
        ))
      ) {
        return
      }

      this.isSubmitting = true
      try {
        const response = await CompaniesAPI.post_CreateAccountMembership(
          this.company.id,
          this.invitee,
        )
        this.sentInviteAccountMembershipIDs.push(response.id)
        this.$messaging.showSuccess(response.message)
        this.resetInvitee()
        this.$emit('account-memberships-updated')
      } catch (error) {
        this.$error.emit(error, true)
      }
      this.isSubmitting = false
    },
  },
})
